import { useCallback, useRef } from 'react'

import Bugsnag from '@bugsnag/js'
import { useMutation } from 'react-query'

import { LogoutResponse } from '@cash-web/protos/squareup/franklin/api/logout.pb'
import logger from '@cash-web/shared/util-logger'
import { post } from '@cash-web/shared-util-fetch'

const postLogout: () => Promise<LogoutResponse> = () => post<unknown, LogoutResponse>('/logout', {})

export const useLogout = () => {
  const isLoggingOutRef = useRef(false)
  const { mutateAsync } = useMutation({ mutationFn: postLogout, retry: 3 })

  return useCallback(
    async ({
      redirectToLogin = true,
      returnToSameUrl = true,
      urlParams,
    }: { redirectToLogin?: boolean; returnToSameUrl?: boolean; urlParams?: URLSearchParams } = {}) => {
      const urlSearchParams = urlParams ? urlParams.toString() : ''
      if (isLoggingOutRef.current) {
        return
      }
      isLoggingOutRef.current = true
      Bugsnag.leaveBreadcrumb('Logging out')
      let logoutResponse: LogoutResponse | undefined
      try {
        logoutResponse = await mutateAsync()
      } catch (e) {
        if (e instanceof Error) {
          logger.error(e)
        } else {
          logger.error(new Error('Unknown logout error'))
        }
      }

      try {
        window?.localStorage?.clear?.()
      } catch (e) {
        logger.error(e instanceof Error ? e : new Error('Unknown error clearing local storage'))
      }

      try {
        window?.sessionStorage?.clear?.()
      } catch (e) {
        logger.error(e instanceof Error ? e : new Error('Unknown error clearing session storage'))
      }

      if (redirectToLogin) {
        if (returnToSameUrl) {
          const searchParams = urlSearchParams ? `&${urlSearchParams}` : ''
          window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}${searchParams}`
        } else {
          const searchParams = urlSearchParams ? `?${urlSearchParams}` : ''
          window.location.href = `/login${searchParams}`
        }
      }

      return logoutResponse
    },
    [mutateAsync]
  )
}
